/*
 * Copyright (C) 2017 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that they will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import React, { Component } from 'react';
import classnames from 'classnames';
import { themeable } from '@instructure/ui-themeable';
import { bool, func, node, number, string, oneOf } from 'prop-types';
import { IconArrowUpSolid, IconArrowDownLine } from '@instructure/ui-icons';
import { ScreenReaderContent } from '@instructure/ui-a11y';
const styles = {
  componentId: 'feqxT',
  template: function template(theme) {
    return "\n\n.StickyButton-styles__root {\n  box-sizing: border-box;\n  display: block;\n  border: none;\n  color: ".concat(theme.color || 'inherit', ";\n  background-color: ").concat(theme.background || 'inherit', ";\n  padding: 0;\n  font-size: ").concat(theme.fontSize || 'inherit', ";\n  font-weight: ").concat(theme.fontWeight || 'inherit', ";\n  font-family: ").concat(theme.fontFamily || 'inherit', ";\n  text-transform: ").concat(theme.textTransform || 'inherit', ";\n  line-height: ").concat(theme.lineHeight || 'inherit', ";\n  white-space: nowrap;\n  cursor: pointer;\n  -webkit-user-select: none;\n      -ms-user-select: none;\n          user-select: none;\n  touch-action: manipulation;\n  -webkit-appearance: none;\n     -moz-appearance: none;\n          appearance: none;\n  transition: background-color 0.2s;\n  outline: none;\n  overflow: visible;\n  border-bottom-left-radius: ").concat(theme.borderRadius || 'inherit', ";\n  border-bottom-right-radius: ").concat(theme.borderRadius || 'inherit', ";\n  position: fixed;\n}\n\n  .StickyButton-styles__root::before {\n    content: \"\";\n    box-sizing: border-box;\n    width: calc(100% + 0.5rem);\n    height: calc(100% + 0.5rem);\n    border: ").concat(theme.focusRingWidth || 'inherit', " solid ").concat(theme.focusRingColor || 'inherit', ";\n    position: absolute;\n    top: -0.25rem;\n    \n    left: -0.25rem;\n    border-bottom-left-radius: ").concat(theme.borderRadius || 'inherit', ";\n    border-bottom-right-radius: ").concat(theme.borderRadius || 'inherit', ";\n    transform: scale(0.25);\n    opacity: 0;\n    transition: all 0.2s ease-out;\n  }\n\n  .StickyButton-styles__root:focus::before {\n      opacity: 1;\n      transform: scale(1);\n    }\n\n  .StickyButton-styles__root:focus,\n  .StickyButton-styles__root:hover {\n    background-color: ").concat(theme.backgroundHover || 'inherit', ";\n  }\n\n  .StickyButton-styles__root:focus .StickyButton-styles__icon, .StickyButton-styles__root:hover .StickyButton-styles__icon {\n      transform: translate3d(0, -0.0625rem, 0) scale(1.2);\n    }\n\n  .StickyButton-styles__root[aria-disabled] {\n    cursor: not-allowed;\n    pointer-events: none;\n    opacity: 0.5;\n  }\n\n.StickyButton-styles__icon {\n  display: block;\n  font-size: 0.75rem;\n  -webkit-margin-start: ").concat(theme.iconMargin || 'inherit', ";\n          margin-inline-start: ").concat(theme.iconMargin || 'inherit', ";\n  transform: translate3d(0, -0.0625rem, 0);\n  transition: all 0.2s;\n}\n\n[dir=\"ltr\"] .StickyButton-styles__icon {\n  margin-left: ").concat(theme.iconMargin || 'inherit', ";\n}\n\n[dir=\"rtl\"] .StickyButton-styles__icon {\n  margin-right: ").concat(theme.iconMargin || 'inherit', ";\n}\n\n.StickyButton-styles__direction--up .StickyButton-styles__layout, .StickyButton-styles__direction--down .StickyButton-styles__layout {\n    -webkit-padding-end: ").concat(theme.hasIconRightPadding || 'inherit', ";\n            padding-inline-end: ").concat(theme.hasIconRightPadding || 'inherit', ";\n  }\n\n[dir=\"ltr\"] .StickyButton-styles__direction--up .StickyButton-styles__layout,\n[dir=\"ltr\"] .StickyButton-styles__direction--down .StickyButton-styles__layout {\n    padding-right: ").concat(theme.hasIconRightPadding || 'inherit', ";\n  }\n\n[dir=\"rtl\"] .StickyButton-styles__direction--up .StickyButton-styles__layout,\n[dir=\"rtl\"] .StickyButton-styles__direction--down .StickyButton-styles__layout {\n    padding-left: ").concat(theme.hasIconRightPadding || 'inherit', ";\n  }\n\n.StickyButton-styles__layout {\n  box-sizing: border-box;\n  display: flex;\n  align-items: center;\n  width: 100%;\n  height: 100%;\n  padding: ").concat(theme.padding || 'inherit', ";\n}\n\n.StickyButton-styles__newActivityButton {\n  offset-inline-start: 0;\n  top: 100%;\n  position: absolute;\n}\n\n[dir=\"ltr\"] .StickyButton-styles__newActivityButton {\n  left: 0;\n}\n\n[dir=\"rtl\"] .StickyButton-styles__newActivityButton {\n  right: 0;\n}\n");
  },
  'root': 'StickyButton-styles__root',
  'icon': 'StickyButton-styles__icon',
  'direction--up': 'StickyButton-styles__direction--up',
  'layout': 'StickyButton-styles__layout',
  'direction--down': 'StickyButton-styles__direction--down',
  'newActivityButton': 'StickyButton-styles__newActivityButton'
};
import theme from './theme.js';

class StickyButton extends Component {
  constructor() {
    super(...arguments);

    this.handleClick = e => {
      const _this$props = this.props,
            disabled = _this$props.disabled,
            onClick = _this$props.onClick;

      if (disabled) {
        e.preventDefault();
        e.stopPropagation();
      } else if (typeof onClick === 'function') {
        onClick(e);
      }
    };
  }

  renderIcon() {
    const direction = this.props.direction;

    if (direction === 'up') {
      return React.createElement(IconArrowUpSolid, {
        className: styles.icon
      });
    } else if (direction === 'down') {
      return React.createElement(IconArrowDownLine, {
        className: styles.icon
      });
    } else {
      return null;
    }
  }

  get descriptionId() {
    return "".concat(this.props.id, "_desc");
  }

  renderDescription() {
    if (this.props.description) {
      return React.createElement(ScreenReaderContent, {
        id: this.descriptionId
      }, this.props.description);
    }

    return null;
  }

  render() {
    const _this$props2 = this.props,
          id = _this$props2.id,
          children = _this$props2.children,
          disabled = _this$props2.disabled,
          hidden = _this$props2.hidden,
          direction = _this$props2.direction,
          zIndex = _this$props2.zIndex;
    const classes = {
      [styles.root]: true,
      [styles['direction--' + direction]]: direction !== 'none'
    };
    const style = {
      zIndex: zIndex || null
    };
    return React.createElement("span", null, React.createElement("button", {
      id: id,
      type: "button",
      onClick: this.handleClick,
      className: classnames(classes, styles.newActivityButton),
      style: style,
      "aria-disabled": disabled ? 'true' : null,
      "aria-hidden": hidden ? 'true' : null,
      ref: this.props.buttonRef,
      "aria-describedby": this.props.description ? this.descriptionId : null
    }, React.createElement("span", {
      className: styles.layout
    }, children, this.renderIcon())), this.renderDescription());
  }

}

StickyButton.propTypes = {
  id: string.isRequired,
  children: node.isRequired,
  onClick: func,
  disabled: bool,
  hidden: bool,
  direction: oneOf(['none', 'up', 'down']),
  className: string,
  zIndex: number,
  buttonRef: func,
  description: string
};
StickyButton.defaultProps = {
  direction: 'none',
  offset: '0',
  className: ''
};
export default themeable(theme, styles)(StickyButton);